import { create } from 'zustand';

// Lightweight toast queue. Components call useToastStore.getState()
// .push() to enqueue; <ToastContainer> reads the queue and renders.

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface Toast {
  id: string;
  tone: ToastTone;
  message: string;
  /** Durée d'affichage ; 4 s par défaut (spec §7). */
  durationMs?: number;
}

interface ToastState {
  toasts: Toast[];
  push: (toast: Omit<Toast, 'id'>) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

let counter = 0;
const nextId = () => `t-${Date.now()}-${counter++}`;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: ({ tone, message, durationMs }) => {
    const id = nextId();
    set((s) => ({ toasts: [...s.toasts, { id, tone, message }] }));
    // Auto-dismiss after 4s — matches spec §7 ("auto-dismiss after 4s").
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
      }, durationMs ?? 4000);
    }
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

// Convenience non-hook accessor for non-component code.
export const toast = {
  info: (message: string) => useToastStore.getState().push({ tone: 'info', message }),
  success: (message: string) => useToastStore.getState().push({ tone: 'success', message }),
  warning: (message: string) => useToastStore.getState().push({ tone: 'warning', message }),
  danger: (message: string) => useToastStore.getState().push({ tone: 'danger', message }),
};
